import React, { useMemo, useState } from "react";
import { connect } from "react-redux";

import { faEnvelope } from "@fortawesome/pro-solid-svg-icons/faEnvelope";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { ItemType } from "antd/es/menu/hooks/useItems";
import notification from "antd/es/notification";
import Tooltip from "antd/es/tooltip";
import { defineMessages, useIntl } from "react-intl";

import DefaultActivityTypeName from "@mapmycustomers/shared/enum/activity/DefaultActivityTypeName";
import { EntitiesSupportedByEmailFeature } from "@mapmycustomers/shared/types/entity";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { useNonNullableContext } from "@mapmycustomers/shared/util/react";
import { Dropdown, useModal } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import EmailComposeModalsContext from "@app/component/email/creation/EmailComposeModalsContext";
import { getActiveActivityTypes } from "@app/store/activity";
import { checkEmailSupporting } from "@app/store/email/actions";
import { doesOrganizationSupportEmailService } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import sendEmailViaClient from "@app/util/email/sendEmailViaClient";
import useEmailSendVariants from "@app/util/email/useEmailSendVariants";

import styles from "./EmailLink.module.scss";

const messages = defineMessages({
  recipientInaccessibleError: {
    id: "email.emailLink.recipientInaccessibleError",
    defaultMessage:
      "This recipient record exists but is not accessible to you. Contact your administrator for access.",
    description: "Email link recipient inaccessible error",
  },
  recipientNotExistsError: {
    id: "email.emailLink.recipientNotExistsError",
    defaultMessage: "Recipient does not exist in MMC.",
    description: "Email link recipient not exists error",
  },
  tooltip: {
    id: "email.emailLink.tooltip",
    defaultMessage: "Send email",
    description: "Email link tooltip",
  },
});

interface EmailLinkProps {
  allowSendViaEmailClient?: boolean;
  allowSendViaMmcEmail?: boolean;
  className?: string;
  email: null | string | undefined;
  entity?: EntitiesSupportedByEmailFeature;
  onFetchEntity?: () => Promise<EntitiesSupportedByEmailFeature | null | undefined>;
  showIcon?: boolean;
}

const EmailLink = ({
  activityTypes,
  allowSendViaEmailClient,
  allowSendViaMmcEmail,
  className,
  email,
  entity,
  onEmailSupportingCheck,
  onFetchEntity,
  showIcon = true,
}: EmailLinkProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const analyticService = useAnalytics();
  const [modal, contextHolder] = useModal();

  const [menuOpen, setMenuOpen] = useState(false);
  const [entityFetching, setEntityFetching] = useState(false);
  const { onEmailModalShow } = useNonNullableContext(EmailComposeModalsContext);

  const isEmailActivityTypeActive = useMemo(
    () =>
      activityTypes.some(({ active, name }) => name === DefaultActivityTypeName.EMAIL && active),
    [activityTypes]
  );

  const handleOpenLink = useDynamicCallback(() => {
    setMenuOpen(false);
    sendEmailViaClient(
      email ?? "",
      intl,
      entity?.name ?? "",
      isEmailActivityTypeActive ? modal : undefined,
      () => {}
    );
    analyticService?.clicked(["Email from email client"], { email });
  });

  const handleShowViaMmc = useDynamicCallback(() => {
    onEmailSupportingCheck({
      modal,
      successfulCallback: async () => {
        if (entity) {
          onEmailModalShow({ entities: [entity] });
          setMenuOpen(false);
        } else if (onFetchEntity) {
          setEntityFetching(true);
          try {
            const fetchedEntity = await onFetchEntity();
            if (fetchedEntity) {
              onEmailModalShow({ entities: [fetchedEntity] });
            } else if (fetchedEntity === null) {
              notification.error({
                message: intl.formatMessage(messages.recipientInaccessibleError),
              });
            } else {
              notification.error({ message: intl.formatMessage(messages.recipientNotExistsError) });
            }
          } finally {
            setEntityFetching(false);
            setMenuOpen(false);
          }
        }
      },
    });
    analyticService?.clicked(["Email from MMC"], { email });
  });

  const menuItems: ItemType[] = useEmailSendVariants({
    allowSendViaEmailClient,
    allowSendViaMmcEmail,
    email: email ?? "",
    isSendViaMmcLoading: entityFetching,
    onSendViaEmailClient: handleOpenLink,
    onSendViaMmcEmail: handleShowViaMmc,
  });

  if (!email) {
    return <>{""}</>;
  }

  return (
    <div className={className}>
      <Tooltip title={intl.formatMessage(messages.tooltip)}>
        <Dropdown
          disabled={entityFetching}
          menu={{ items: menuItems }}
          onOpenChange={setMenuOpen}
          open={menuOpen}
          trigger={["hover"]}
        >
          <ButtonLink>
            {email}
            {showIcon && (
              <FontAwesomeIcon className={styles.emailIcon} icon={faEnvelope} size="xs" />
            )}
          </ButtonLink>
        </Dropdown>
      </Tooltip>
      {contextHolder}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypes: getActiveActivityTypes(state),
  organizationSupportEmailService: doesOrganizationSupportEmailService(state),
});

const mapDispatchToProps = {
  onEmailSupportingCheck: checkEmailSupporting,
};

export default connect(mapStateToProps, mapDispatchToProps)(EmailLink);
